import React, { useState, useCallback, useEffect } from 'react'
import BigNumber from 'bignumber.js'
import styled from 'styled-components'
import { Text } from 'rebass'
import { Contract } from 'web3-eth-contract'
import { Plus } from 'react-feather'

import { getBalanceNumber } from '../../../utils/bakery/formatBalance'
import { ButtonPrimary } from '../../../components/Button'
import Value from '../../../components/Value'
import WithdrawModal from './WithdrawModal'
import DepositModal from './DepositModal'
import {
  getErc721Balance,
  getBmoncNftBalance,
  getNftHelperContract,
  commonStakeERC721EarnERC20GetUserInfo,
  commonStakeERC721EarnBakeGetUserInfo,
  getBmoncNFTContract,
} from '../../../sushi/utils'
import { 
  simpleStakeERC721EarnERC20GetUserInfo,
  simpleStakeERC721EarnERC20CanStake
} from '../../../sushi/simpleStakeERC721EarnERC20'

// hooks
import { useActiveWeb3React } from '../../../hooks'
import useYam from '../../../hooks/bakery/useYam'
import useAllowanceAll from '../../../hooks/nft/useAllowanceAll'
import useApproveAllERC721 from '../../../hooks/nft/useApproveAllERC721'
import useModal from '../../../hooks/bakery/useModal'
import useStakeNFT from '../../../hooks/bakery/useStakeNFT'
import useUnstakeNFT from '../../../hooks/bakery/useUnstakeNFT'

import { MINTTOKEN } from '../../../constants/bakery'
import { poolMasterContract } from '../../../userType/poolMasterContract'

const AmountElement = styled(Text)`
  color: ${({ theme }) => theme.bsText4};
`
const EarnBtn = styled(ButtonPrimary)`
  position: relative;
  flex: 1;
  font-family: Baloo Da;
  height: 48px;
  padding: 0 16px;
  margin-top: 33px;
`
const AddBtn = styled(ButtonPrimary)`
  flex-shrink: 0;
  font-family: Baloo Da;
  width: 48px;
  height: 48px;
  margin-left: 16px;
  padding: 0;
  margin-top: 33px;
`
const BtnWrpper = styled.div`
  display: flex;
  align-items: center;
  margin-top: '40px';
`
const StyledPlusIcon = styled(Plus)`
  height: 20px;
  width: 20px;

  > * {
    stroke: ${({ theme }) => theme.primaryText1};
  }
`

interface StakeNFTProps {
  name: string
  pid: number
  lpContract: Contract
  lpTokenAddress: string
  lpTokenName: string
  earnTokenName: string
}

// eslint-disable-next-line
const StakeNFT: React.FC<StakeNFTProps> = ({ name, lpContract, lpTokenAddress, pid, lpTokenName, earnTokenName }) => {
  const [requestedApproval, setRequestedApproval] = useState(false)
  const [stakeSuccess, setStakeSuccess] = useState(false)
  const sushi = useYam()
  const { chainId, account } = useActiveWeb3React()
  // const account = '0xbc2f255bc129c1eda221c1cc7228398f61ebbe93'
  let masterContract: Contract = poolMasterContract(sushi, earnTokenName)

  const emyptArr: any[] = []
  const [balanceArr, setBalanceArr] = useState(emyptArr)
  const [stakedBalanceArr, setStakedBalanceArr] = useState([])
  const [stakedBalance, setStakedBalance] = useState(new BigNumber(0))
  const [tokenBalance, setTokenBalance] = useState(new BigNumber(0))
  const nftHelper = getNftHelperContract(sushi)
  const allowanceAll = useAllowanceAll({type: earnTokenName})
  const onApprove = useApproveAllERC721(earnTokenName)
  const { onStakeNFT } = useStakeNFT(lpTokenAddress, masterContract, earnTokenName)
  const { onUnstake } = useUnstakeNFT(lpTokenAddress, masterContract, earnTokenName)

  const getBalanceArr = useCallback(async() => {
    const arr = await getErc721Balance(nftHelper, lpTokenAddress, account)
    
    let canStakeArr
    if ([MINTTOKEN.TOYNFTSTAKE].includes(earnTokenName)) {
      const isCanStake = await simpleStakeERC721EarnERC20CanStake(masterContract, arr)
      const statusArr = arr.filter((d: any, i: number) => {
        return isCanStake[i]
      })
      canStakeArr = [...statusArr]
    } else {
      canStakeArr = [...arr]
    }
    const balance = new BigNumber(canStakeArr.length).times(new BigNumber(10).pow(18))
    setBalanceArr(canStakeArr)
    setTokenBalance(balance)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, lpTokenAddress, nftHelper])

  const getStakeInfo = useCallback(async() => {
    let stakeInfo: any[] = []
    switch (earnTokenName) {
      case MINTTOKEN.TKONFTSTAKE:
      case MINTTOKEN.BMONCNFTSTAKE:
        stakeInfo = await commonStakeERC721EarnERC20GetUserInfo(masterContract, lpTokenAddress, account)
        break
      case MINTTOKEN.TOYNFTSTAKE:
        stakeInfo = await simpleStakeERC721EarnERC20GetUserInfo(masterContract, account)
        break
      case MINTTOKEN.BMMNFTSTAKE:
        stakeInfo = await commonStakeERC721EarnBakeGetUserInfo(masterContract, account)
        break
      default:
        stakeInfo = await simpleStakeERC721EarnERC20GetUserInfo(masterContract, account)
    }
    const length = new BigNumber(stakeInfo[2].length).times(new BigNumber(10).pow(18))
    setStakedBalance(length)
    setStakedBalanceArr(stakeInfo[2])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, lpTokenAddress, masterContract])


  const handleApprove = useCallback(async () => {
    try {
      setRequestedApproval(true)
      const txHash = await onApprove()
      // user rejected tx or didn't go thru
      if (!txHash) {
        setRequestedApproval(false)
      }
    } catch (e) {
      console.log(e)
    }
  }, [onApprove, setRequestedApproval])

  const handleStake = useCallback(async (amount: string) => {
    let spliceIndex = Number(amount)
    if (balanceArr.length < spliceIndex) {
      spliceIndex = balanceArr.length
    }
    const tokenIds = balanceArr.slice(0, spliceIndex)
    const txHash = await onStakeNFT(tokenIds)
    if (txHash) {
      setStakeSuccess(!stakeSuccess)
    }
  }, [balanceArr, onStakeNFT, stakeSuccess])

  const handleUnStake = useCallback(async(amount: string) => {
    let spliceIndex = Number(amount)
    if (stakedBalanceArr.length < spliceIndex) {
      spliceIndex = stakedBalanceArr.length
    }
    const tokenIds = stakedBalanceArr.slice(0, spliceIndex)
    const txHash = await onUnstake(tokenIds)
    if (txHash) {
      setStakeSuccess(!stakeSuccess)
    }
  }, [stakedBalanceArr, onUnstake, stakeSuccess])

  const [onPresentWithdraw] = useModal(
    <WithdrawModal 
      max={stakedBalance} 
      onConfirm={handleUnStake} 
      tokenName={lpTokenName}
      integer={true}
    />
  )
  const [onPresentDeposit] = useModal(
    <DepositModal 
      max={tokenBalance} 
      onConfirm={handleStake} 
      tokenName={lpTokenName}
      integer={true}
    />)
  
  // 查询BmoncNFT余额
  const fetchBmoncNftBalance = useCallback(async() => {
    let canStakeArr = await getBmoncNftBalance(lpContract, getBmoncNFTContract(sushi), account)
    const balance = new BigNumber(canStakeArr.length).times(new BigNumber(10).pow(18))
    setBalanceArr(canStakeArr)
    setTokenBalance(balance) 
  }, [getBmoncNftBalance, getBmoncNFTContract])
    

  useEffect(() => {
    if(earnTokenName === MINTTOKEN.BMONCNFTSTAKE && chainId && chainId === 56){
      // 查询BmoncNFT余额
      fetchBmoncNftBalance()
    }else{
      getBalanceArr()
    }
    getStakeInfo()
  }, [account, stakeSuccess, getBalanceArr, getBmoncNftBalance, getBmoncNFTContract, getStakeInfo, chainId])
  
  return (
    <>
      <AmountElement fontSize={36} fontWeight={700}>
        <Value value={getBalanceNumber(stakedBalance)} />
      </AmountElement>
      <Text fontSize={14} fontFamily={'Baloo Da'} fontWeight={600}>
        {lpTokenName} Tokens Staked
      </Text>
      <BtnWrpper>
        {!allowanceAll ?
          <EarnBtn disabled={requestedApproval} onClick={handleApprove}>
            <Text fontSize={14} fontWeight={700}>{`Approve ${lpTokenName}`}</Text>
          </EarnBtn>
        : (
          <>
            <EarnBtn
              disabled={stakedBalance.eq(new BigNumber(0))}
              onClick={onPresentWithdraw}
            >
              <Text fontSize={14} fontWeight={700}>
                Unstake
              </Text>
            </EarnBtn>
            <AddBtn
              onClick={onPresentDeposit}
              disabled={name === 'Pet Airdrop'}
            >
              <StyledPlusIcon />
            </AddBtn>
          </>
        )}
      </BtnWrpper>
    </>
  )
}

export default StakeNFT
